import { useEffect, useState } from "react";
import { Save, Store, Loader2, Bell, Plus, Trash2, Refrigerator, Send, Smartphone, AlertTriangle, RotateCcw, KeyRound, AtSign, Eye, EyeOff } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail } from "../lib/api";
import { toast } from "sonner";
import { useAuth } from "../lib/auth";
import { getReminderConfig, saveReminderConfig, ensurePermission } from "../lib/reminders";
import { subscribeToPush, unsubscribeFromPush, getPushStatus, sendTestPush, isPushSupported, updatePushTimes } from "../lib/push";
import InstallAppButton from "../components/InstallAppButton";

const FIELDS = [
    { k: "ragione_sociale", l: "Ragione sociale / Nome attività" },
    { k: "tipo_attivita", l: "Tipo di attività" },
    { k: "titolare", l: "Titolare" },
    { k: "rappresentante_legale", l: "Rappresentante legale" },
    { k: "responsabile_haccp", l: "Responsabile HACCP" },
    { k: "indirizzo", l: "Indirizzo operativo (negozio)" },
    { k: "citta", l: "Città" },
    { k: "cap", l: "CAP" },
    { k: "provincia", l: "Provincia" },
    { k: "sede_legale", l: "Sede legale (se diversa)" },
    { k: "partita_iva", l: "Partita IVA" },
    { k: "codice_fiscale", l: "Codice Fiscale" },
    { k: "numero_rea", l: "N° REA (Camera di Commercio)" },
    { k: "codice_ateco", l: "Codice ATECO" },
    { k: "telefono", l: "Telefono" },
    { k: "email", l: "Email" },
];

export default function Impostazioni({ onShopUpdate }) {
    const [s, setS] = useState(null);
    const [saving, setSaving] = useState(false);
    const [resetting, setResetting] = useState(false);
    const [deleting, setDeleting] = useState(false);
    const [reminders, setReminders] = useState(getReminderConfig());
    const { user, logout } = useAuth();
    const nav = useNavigate();
    const [permission, setPermission] = useState(
        typeof window !== "undefined" && "Notification" in window ? Notification.permission : "unsupported",
    );
    const [pushStatus, setPushStatus] = useState({ supported: false });
    const [testingPush, setTestingPush] = useState(false);

    // ---------- Credentials state ----------
    const [credBusy, setCredBusy] = useState(false);
    const [newEmail, setNewEmail] = useState("");
    const [emailPwd, setEmailPwd] = useState("");
    const [showEmailPwd, setShowEmailPwd] = useState(false);
    const [curPwd, setCurPwd] = useState("");
    const [newPwd, setNewPwd] = useState("");
    const [showCurPwd, setShowCurPwd] = useState(false);
    const [showNewPwd, setShowNewPwd] = useState(false);

    async function changeEmail() {
        if (!newEmail || !emailPwd) {
            toast.error("Inserisci la nuova email e la password attuale");
            return;
        }
        setCredBusy(true);
        try {
            await api.post("/account/change-email", { new_email: newEmail, current_password: emailPwd });
            toast.success("Email aggiornata. Esegui di nuovo il login.");
            setNewEmail("");
            setEmailPwd("");
            setTimeout(async () => {
                await logout();
                nav("/login", { replace: true });
            }, 1200);
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
        } finally {
            setCredBusy(false);
        }
    }

    async function changePassword() {
        if (!curPwd || !newPwd) {
            toast.error("Compila entrambi i campi password");
            return;
        }
        if (newPwd.length < 6) {
            toast.error("La nuova password deve avere almeno 6 caratteri");
            return;
        }
        setCredBusy(true);
        try {
            await api.post("/account/change-password", { current_password: curPwd, new_password: newPwd });
            toast.success("Password aggiornata. Esegui di nuovo il login.");
            setCurPwd("");
            setNewPwd("");
            setTimeout(async () => {
                await logout();
                nav("/login", { replace: true });
            }, 1200);
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
        } finally {
            setCredBusy(false);
        }
    }

    useEffect(() => {
        api.get("/settings").then((r) => setS(r.data));
        getPushStatus().then(setPushStatus);
    }, []);

    function updReminders(next) {
        setReminders(next);
        saveReminderConfig(next);
        // If already subscribed, sync new times to backend
        if (pushStatus.subscribed) {
            updatePushTimes(next.times).catch(() => null);
        }
    }

    async function requestPerm() {
        const res = await ensurePermission();
        setPermission(res);
        if (res === "granted") {
            toast.success("Notifiche autorizzate");
            try {
                if (isPushSupported()) {
                    await subscribeToPush(reminders.times);
                    setPushStatus(await getPushStatus());
                    toast.success("Push registrato sul dispositivo");
                }
            } catch (e) {
                toast.error("Errore push: " + (e?.message || e));
            }
        } else if (res === "denied") toast.error("Notifiche bloccate dal browser");
    }

    async function unsubscribe() {
        await unsubscribeFromPush();
        setPushStatus(await getPushStatus());
        toast.success("Disiscritto dalle notifiche push");
    }

    async function testPush() {
        setTestingPush(true);
        try {
            const r = await sendTestPush();
            toast.success(`Test inviato a ${r.sent}/${r.total_subs} dispositivi`);
        } catch (e) {
            toast.error("Errore invio test");
        } finally {
            setTestingPush(false);
        }
    }

    async function save() {
        setSaving(true);
        try {
            await api.put("/settings", s);
            toast.success("Dati salvati");
        } catch {
            toast.error("Errore salvataggio");
        } finally {
            setSaving(false);
        }
    }

    if (!s) return <div className="card-soft p-6 h-32 animate-pulse" />;

    return (
        <div className="space-y-6 pb-6" data-testid="settings-page">
            <section className="card-soft p-6">
                <div className="flex items-center gap-3 mb-5">
                    <div className="w-12 h-12 rounded-xl bg-forest text-white grid place-items-center">
                        <Store size={20} />
                    </div>
                    <div>
                        <h2 className="font-display text-xl font-semibold text-forest-dark">Dati del negozio</h2>
                        <p className="text-sm text-stone-500">Verranno usati nei report e nel manuale HACCP.</p>
                    </div>
                </div>

                <div className="grid sm:grid-cols-2 gap-4">
                    {FIELDS.map(({ k, l }) => (
                        <label key={k} className="block">
                            <span className="text-xs font-semibold text-stone-600">{l}</span>
                            <input
                                className="input-soft mt-1"
                                value={s[k] || ""}
                                onChange={(e) => setS({ ...s, [k]: e.target.value })}
                                data-testid={`field-${k}`}
                            />
                        </label>
                    ))}
                </div>

                <button
                    onClick={save}
                    disabled={saving}
                    className="btn-primary mt-6 inline-flex items-center gap-2"
                    data-testid="btn-save-settings"
                >
                    {saving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}
                    Salva impostazioni
                </button>
            </section>

            <section className="card-soft p-6" data-testid="frigoriferi-section">
                <div className="flex items-center gap-3 mb-5">
                    <div className="w-12 h-12 rounded-xl bg-forest-50 text-forest grid place-items-center">
                        <Refrigerator size={20} />
                    </div>
                    <div className="flex-1">
                        <h2 className="font-display text-xl font-semibold text-forest-dark">
                            Frigoriferi e celle del negozio
                        </h2>
                        <p className="text-sm text-stone-500">
                            Aggiungi quanti frigoriferi hai: per ognuno verranno creati 2 controlli temperatura
                            (mattina e pomeriggio) nella checklist HACCP.
                        </p>
                    </div>
                </div>

                <div className="space-y-3" data-testid="frigoriferi-list">
                    {(s.frigoriferi || []).map((f, idx) => (
                        <div
                            key={f.id || idx}
                            className="bg-bone-2 rounded-xl p-4 flex flex-wrap items-end gap-3"
                            data-testid={`frigo-row-${idx}`}
                        >
                            <label className="block flex-1 min-w-[180px]">
                                <span className="text-xs font-semibold text-stone-600">Nome / posizione</span>
                                <input
                                    className="input-soft mt-1"
                                    value={f.nome || ""}
                                    placeholder="Es. Frigo banco, Cella, Vetrina..."
                                    onChange={(e) => {
                                        const next = [...s.frigoriferi];
                                        next[idx] = { ...f, nome: e.target.value };
                                        setS({ ...s, frigoriferi: next });
                                    }}
                                    data-testid={`frigo-name-${idx}`}
                                />
                            </label>
                            <label className="block w-24">
                                <span className="text-xs font-semibold text-stone-600">Min °C</span>
                                <input
                                    type="number"
                                    step="0.1"
                                    className="input-soft mt-1"
                                    value={f.min_c ?? 0}
                                    onChange={(e) => {
                                        const next = [...s.frigoriferi];
                                        next[idx] = { ...f, min_c: parseFloat(e.target.value) || 0 };
                                        setS({ ...s, frigoriferi: next });
                                    }}
                                    data-testid={`frigo-min-${idx}`}
                                />
                            </label>
                            <label className="block w-24">
                                <span className="text-xs font-semibold text-stone-600">Max °C</span>
                                <input
                                    type="number"
                                    step="0.1"
                                    className="input-soft mt-1"
                                    value={f.max_c ?? 8}
                                    onChange={(e) => {
                                        const next = [...s.frigoriferi];
                                        next[idx] = { ...f, max_c: parseFloat(e.target.value) || 0 };
                                        setS({ ...s, frigoriferi: next });
                                    }}
                                    data-testid={`frigo-max-${idx}`}
                                />
                            </label>
                            <label className="block w-24" title="Quanti gradi prima del limite vuoi essere avvisato (warning giallo).">
                                <span className="text-xs font-semibold text-stone-600">Buffer °C</span>
                                <input
                                    type="number"
                                    step="0.1"
                                    className="input-soft mt-1"
                                    value={f.buffer_c ?? 1}
                                    onChange={(e) => {
                                        const next = [...s.frigoriferi];
                                        next[idx] = { ...f, buffer_c: parseFloat(e.target.value) || 0 };
                                        setS({ ...s, frigoriferi: next });
                                    }}
                                    data-testid={`frigo-buffer-${idx}`}
                                />
                            </label>
                            <button
                                onClick={() => {
                                    const next = s.frigoriferi.filter((_, i) => i !== idx);
                                    setS({ ...s, frigoriferi: next });
                                }}
                                className="p-2 text-stone-400 hover:text-red-600 self-end"
                                data-testid={`frigo-del-${idx}`}
                                aria-label="Elimina frigorifero"
                            >
                                <Trash2 size={16} />
                            </button>
                        </div>
                    ))}
                </div>

                <button
                    onClick={() => {
                        const newFrigo = {
                            id: `f_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
                            nome: "",
                            min_c: 0,
                            max_c: 8,
                            buffer_c: 1,
                        };
                        setS({ ...s, frigoriferi: [...(s.frigoriferi || []), newFrigo] });
                    }}
                    className="btn-ghost mt-3 inline-flex items-center gap-2 text-sm"
                    data-testid="btn-add-frigo"
                >
                    <Plus size={16} /> Aggiungi frigorifero
                </button>

                <p className="text-xs text-stone-500 mt-4">
                    💡 Ricordati di premere "Salva impostazioni" qui sopra dopo aver modificato la lista.
                </p>
            </section>

            <section className="card-soft p-6" data-testid="reminders-section">
                <div className="flex items-center gap-3 mb-5">
                    <div className="w-12 h-12 rounded-xl bg-[#E9A846]/20 text-[#A87214] grid place-items-center">
                        <Bell size={20} />
                    </div>
                    <div>
                        <h2 className="font-display text-xl font-semibold text-forest-dark">
                            Promemoria giornalieri pulizia e igiene
                        </h2>
                        <p className="text-sm text-stone-500">
                            Ricevi notifiche per ricordarti di compilare la checklist HACCP. Tieni l'app aperta nel browser.
                        </p>
                    </div>
                </div>

                <label className="flex items-center gap-3 mb-4">
                    <input
                        type="checkbox"
                        checked={reminders.enabled}
                        onChange={(e) => updReminders({ ...reminders, enabled: e.target.checked })}
                        data-testid="reminders-enabled"
                        className="w-5 h-5"
                    />
                    <span className="font-semibold text-stone-700">Attiva promemoria</span>
                </label>

                <div className="space-y-2 mb-4">
                    <p className="text-xs font-semibold text-stone-600 uppercase tracking-wider">
                        Preset rapidi (in base al tuo turno di lavoro)
                    </p>
                    <div className="flex flex-wrap gap-2 mb-3">
                        <button
                            type="button"
                            onClick={() => updReminders({ ...reminders, times: ["09:00", "17:00"] })}
                            className="px-3 py-1.5 rounded-full bg-bone-2 hover:bg-forest-50 text-stone-700 text-xs font-semibold border border-soft"
                            data-testid="reminders-preset-day"
                        >
                            ☀️ Turno diurno (09:00, 17:00)
                        </button>
                        <button
                            type="button"
                            onClick={() => updReminders({ ...reminders, times: ["07:30", "12:30", "19:00"] })}
                            className="px-3 py-1.5 rounded-full bg-bone-2 hover:bg-forest-50 text-stone-700 text-xs font-semibold border border-soft"
                            data-testid="reminders-preset-ristorante"
                        >
                            🍽️ Ristorante (07:30, 12:30, 19:00)
                        </button>
                        <button
                            type="button"
                            onClick={() => updReminders({ ...reminders, times: ["22:00", "03:00"] })}
                            className="px-3 py-1.5 rounded-full bg-bone-2 hover:bg-forest-50 text-stone-700 text-xs font-semibold border border-soft"
                            data-testid="reminders-preset-night"
                        >
                            🌙 Turno notturno (22:00, 03:00)
                        </button>
                        <button
                            type="button"
                            onClick={() => updReminders({ ...reminders, times: ["06:00", "14:00", "22:00"] })}
                            className="px-3 py-1.5 rounded-full bg-bone-2 hover:bg-forest-50 text-stone-700 text-xs font-semibold border border-soft"
                            data-testid="reminders-preset-3shifts"
                        >
                            🔄 3 turni (06, 14, 22)
                        </button>
                    </div>
                    <p className="text-xs font-semibold text-stone-600 uppercase tracking-wider">
                        Orari di notifica ({reminders.times.length})
                    </p>
                    {reminders.times.map((t, idx) => (
                        <div key={idx} className="flex items-center gap-2" data-testid={`reminder-time-${idx}`}>
                            <input
                                type="time"
                                value={t}
                                onChange={(e) => {
                                    const next = [...reminders.times];
                                    next[idx] = e.target.value;
                                    updReminders({ ...reminders, times: next });
                                }}
                                className="input-soft max-w-[140px]"
                                data-testid={`reminder-input-${idx}`}
                            />
                            {reminders.times.length > 1 && (
                                <button
                                    onClick={() => {
                                        const next = reminders.times.filter((_, i) => i !== idx);
                                        updReminders({ ...reminders, times: next });
                                    }}
                                    className="p-2 text-stone-400 hover:text-red-600"
                                    data-testid={`reminder-del-${idx}`}
                                    aria-label="Elimina orario"
                                >
                                    <Trash2 size={16} />
                                </button>
                            )}
                        </div>
                    ))}
                    <button
                        onClick={() => updReminders({ ...reminders, times: [...reminders.times, "12:00"] })}
                        className="btn-ghost text-xs inline-flex items-center gap-1"
                        data-testid="reminder-add"
                    >
                        <Plus size={14} /> Aggiungi orario
                    </button>
                </div>

                {permission !== "granted" && (
                    <div className="flex items-center justify-between gap-3 bg-[#FFF8EE] border border-[#E9A846]/30 rounded-xl p-3">
                        <p className="text-xs text-stone-700">
                            {permission === "denied"
                                ? "Le notifiche sono bloccate dalle impostazioni del browser."
                                : "Devi autorizzare le notifiche del browser."}
                        </p>
                        {permission !== "denied" && (
                            <button onClick={requestPerm} className="btn-accent text-xs" data-testid="reminder-permission">
                                Attiva ora
                            </button>
                        )}
                    </div>
                )}
                {permission === "granted" && (
                    <p className="text-xs text-emerald-700 bg-emerald-50 rounded-xl p-3">
                        ✓ Notifiche autorizzate{pushStatus.subscribed ? " e push attive a app chiusa" : " (solo con app aperta)"}.
                    </p>
                )}

                {pushStatus.supported && (
                    <div className="mt-4 border-t border-soft pt-4">
                        <div className="flex items-center gap-2 mb-2">
                            <Smartphone size={16} className="text-forest" />
                            <p className="font-semibold text-forest-dark text-sm">Notifiche push (anche a app chiusa)</p>
                        </div>
                        <p className="text-xs text-stone-500 mb-3">
                            Installa l'app sulla schermata home del telefono per ricevere i promemoria come notifiche di sistema, anche quando il browser è chiuso.
                        </p>
                        <div className="flex flex-wrap gap-2 mb-3">
                            <InstallAppButton />
                            <InstallAppButton variant="badge" />
                        </div>
                        <div className="flex flex-wrap gap-2">
                            {pushStatus.subscribed ? (
                                <>
                                    <button onClick={testPush} disabled={testingPush} className="btn-primary text-xs inline-flex items-center gap-1" data-testid="push-test">
                                        {testingPush ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
                                        Invia notifica di prova
                                    </button>
                                    <button onClick={unsubscribe} className="btn-ghost text-xs" data-testid="push-unsubscribe">
                                        Disabilita push
                                    </button>
                                </>
                            ) : (
                                permission === "granted" && (
                                    <button onClick={requestPerm} className="btn-accent text-xs" data-testid="push-subscribe">
                                        Attiva push su questo dispositivo
                                    </button>
                                )
                            )}
                        </div>
                    </div>
                )}
            </section>

            {/* ACCOUNT CREDENTIALS */}
            <section className="card-soft p-6" data-testid="account-credentials">
                <div className="flex items-center gap-3 mb-1">
                    <div className="w-12 h-12 rounded-xl bg-forest-50 text-forest grid place-items-center">
                        <KeyRound size={22} />
                    </div>
                    <div>
                        <h2 className="font-display text-xl text-forest-dark">Credenziali account</h2>
                        <p className="text-sm text-stone-500">
                            Email attuale: <b className="text-stone-700">{user?.email}</b>
                        </p>
                    </div>
                </div>

                <div className="mt-5 grid lg:grid-cols-2 gap-4">
                    {/* Change email */}
                    <div className="rounded-xl border border-soft p-4">
                        <div className="flex items-center gap-2 mb-3">
                            <AtSign size={18} className="text-forest" />
                            <h3 className="font-semibold text-forest-dark">Cambia email</h3>
                        </div>
                        <div className="space-y-3">
                            <label className="block">
                                <span className="text-xs font-semibold text-stone-600">Nuova email</span>
                                <input
                                    type="email"
                                    className="input-soft mt-1"
                                    placeholder="nuova@email.it"
                                    value={newEmail}
                                    onChange={(e) => setNewEmail(e.target.value)}
                                    data-testid="cred-new-email"
                                    autoComplete="off"
                                />
                            </label>
                            <label className="block">
                                <span className="text-xs font-semibold text-stone-600">Password attuale (per conferma)</span>
                                <div className="relative mt-1">
                                    <input
                                        type={showEmailPwd ? "text" : "password"}
                                        className="input-soft pr-10"
                                        value={emailPwd}
                                        onChange={(e) => setEmailPwd(e.target.value)}
                                        data-testid="cred-email-pwd"
                                        autoComplete="current-password"
                                    />
                                    <button
                                        type="button"
                                        onClick={() => setShowEmailPwd((v) => !v)}
                                        className="absolute right-2 top-1/2 -translate-y-1/2 text-stone-500"
                                        tabIndex={-1}
                                    >
                                        {showEmailPwd ? <EyeOff size={16} /> : <Eye size={16} />}
                                    </button>
                                </div>
                            </label>
                            <button
                                onClick={changeEmail}
                                disabled={credBusy}
                                className="btn-primary inline-flex items-center gap-2"
                                data-testid="cred-save-email"
                            >
                                {credBusy ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}
                                Aggiorna email
                            </button>
                            <p className="text-xs text-stone-500">
                                Dopo il cambio email farai automaticamente logout.
                            </p>
                        </div>
                    </div>

                    {/* Change password */}
                    <div className="rounded-xl border border-soft p-4">
                        <div className="flex items-center gap-2 mb-3">
                            <KeyRound size={18} className="text-forest" />
                            <h3 className="font-semibold text-forest-dark">Cambia password</h3>
                        </div>
                        <div className="space-y-3">
                            <label className="block">
                                <span className="text-xs font-semibold text-stone-600">Password attuale</span>
                                <div className="relative mt-1">
                                    <input
                                        type={showCurPwd ? "text" : "password"}
                                        className="input-soft pr-10"
                                        value={curPwd}
                                        onChange={(e) => setCurPwd(e.target.value)}
                                        data-testid="cred-current-pwd"
                                        autoComplete="current-password"
                                    />
                                    <button
                                        type="button"
                                        onClick={() => setShowCurPwd((v) => !v)}
                                        className="absolute right-2 top-1/2 -translate-y-1/2 text-stone-500"
                                        tabIndex={-1}
                                    >
                                        {showCurPwd ? <EyeOff size={16} /> : <Eye size={16} />}
                                    </button>
                                </div>
                            </label>
                            <label className="block">
                                <span className="text-xs font-semibold text-stone-600">Nuova password (min. 6 caratteri)</span>
                                <div className="relative mt-1">
                                    <input
                                        type={showNewPwd ? "text" : "password"}
                                        className="input-soft pr-10"
                                        value={newPwd}
                                        onChange={(e) => setNewPwd(e.target.value)}
                                        data-testid="cred-new-pwd"
                                        autoComplete="new-password"
                                    />
                                    <button
                                        type="button"
                                        onClick={() => setShowNewPwd((v) => !v)}
                                        className="absolute right-2 top-1/2 -translate-y-1/2 text-stone-500"
                                        tabIndex={-1}
                                    >
                                        {showNewPwd ? <EyeOff size={16} /> : <Eye size={16} />}
                                    </button>
                                </div>
                            </label>
                            <button
                                onClick={changePassword}
                                disabled={credBusy}
                                className="btn-primary inline-flex items-center gap-2"
                                data-testid="cred-save-pwd"
                            >
                                {credBusy ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}
                                Aggiorna password
                            </button>
                            <p className="text-xs text-stone-500">
                                Dopo il cambio password farai automaticamente logout.
                            </p>
                        </div>
                    </div>
                </div>
            </section>

            {/* DANGER ZONE */}
            <section className="card-soft p-6 border-2 border-red-200" data-testid="danger-zone">
                <div className="flex items-center gap-3 mb-1">
                    <div className="w-12 h-12 rounded-xl bg-red-100 text-red-600 grid place-items-center">
                        <AlertTriangle size={22} />
                    </div>
                    <div>
                        <h2 className="font-display text-xl text-red-700">Zona pericolosa</h2>
                        <p className="text-sm text-stone-500">Azioni irreversibili. Procedi con cautela.</p>
                    </div>
                </div>

                <div className="mt-5 space-y-4">
                    {/* Reset workspace */}
                    <div className="rounded-xl border border-orange-200 bg-orange-50/50 p-4">
                        <div className="flex items-start gap-3 mb-3">
                            <RotateCcw size={20} className="text-orange-600 shrink-0 mt-0.5" />
                            <div>
                                <h3 className="font-semibold text-orange-900">Ricomincia da capo</h3>
                                <p className="text-sm text-stone-600 mt-1">
                                    Cancella tutti i dati del tuo negozio (HACCP, fatture, spese, scadenze, documenti)
                                    e riporta l'app all'onboarding iniziale per scegliere un nuovo tipo di attività.
                                    <br />
                                    <b>Il tuo account, password e abbonamento restano intatti.</b>
                                </p>
                            </div>
                        </div>
                        <button
                            onClick={async () => {
                                const confirm1 = window.prompt(
                                    "ATTENZIONE: questa azione cancellerà TUTTI i dati del tuo negozio (HACCP, fatture, spese, scadenze, documenti).\n\nPer confermare, scrivi: RESET",
                                );
                                if (confirm1 !== "RESET") return;
                                setResetting(true);
                                try {
                                    const r = await api.post("/account/reset-workspace", {});
                                    const total = Object.values(r.data.wiped || {}).reduce((a, b) => a + b, 0);
                                    toast.success(`Workspace resettato (${total} elementi eliminati)`);
                                    if (onShopUpdate) await onShopUpdate();
                                    setTimeout(() => nav("/", { replace: true }), 500);
                                } catch (e) {
                                    toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
                                } finally {
                                    setResetting(false);
                                }
                            }}
                            disabled={resetting}
                            className="inline-flex items-center gap-2 bg-orange-600 hover:bg-orange-700 text-white px-4 py-2.5 rounded-xl text-sm font-medium transition-colors disabled:opacity-60"
                            data-testid="reset-workspace-btn"
                        >
                            {resetting ? <Loader2 size={16} className="animate-spin" /> : <RotateCcw size={16} />}
                            {resetting ? "Reset in corso..." : "Ricomincia da capo"}
                        </button>
                    </div>

                    {/* Delete account */}
                    {user?.role !== "admin" && (
                        <div className="rounded-xl border border-red-200 bg-red-50/50 p-4">
                            <div className="flex items-start gap-3 mb-3">
                                <Trash2 size={20} className="text-red-600 shrink-0 mt-0.5" />
                                <div>
                                    <h3 className="font-semibold text-red-900">Elimina account</h3>
                                    <p className="text-sm text-stone-600 mt-1">
                                        Cancella definitivamente il tuo account HACCP Manager e tutti i dati associati.
                                        Sarai disconnesso. Potrai poi registrarti di nuovo con la stessa email.
                                        <br />
                                        <b>Questa azione non è reversibile.</b>
                                    </p>
                                </div>
                            </div>
                            <button
                                onClick={async () => {
                                    const confirm1 = window.prompt(
                                        "ATTENZIONE: questa azione cancellerà definitivamente il tuo account e TUTTI i dati associati.\n\nPer confermare, scrivi: ELIMINA",
                                    );
                                    if (confirm1 !== "ELIMINA") return;
                                    setDeleting(true);
                                    try {
                                        await api.delete("/account");
                                        toast.success("Account eliminato");
                                        await logout();
                                        nav("/", { replace: true });
                                    } catch (e) {
                                        toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
                                        setDeleting(false);
                                    }
                                }}
                                disabled={deleting}
                                className="inline-flex items-center gap-2 bg-red-600 hover:bg-red-700 text-white px-4 py-2.5 rounded-xl text-sm font-medium transition-colors disabled:opacity-60"
                                data-testid="delete-account-btn"
                            >
                                {deleting ? <Loader2 size={16} className="animate-spin" /> : <Trash2 size={16} />}
                                {deleting ? "Eliminazione in corso..." : "Elimina il mio account"}
                            </button>
                        </div>
                    )}
                </div>
            </section>
        </div>
    );
}
